import * as ExpoCrypto from 'expo-crypto';
import { capturerIncident } from '../services/sentry';

// Force le PKCE de supabase-js en S256 sur React Native / Hermes.
//
// auth-js choisit s256 vs plain UNIQUEMENT selon la présence de crypto.subtle (+ TextEncoder + btoa)
// au runtime (cf. generatePKCEChallenge). Hermes ne fournit pas crypto.subtle → repli silencieux en
// `plain`, où le code_challenge == code_verifier : interceptable via un scheme custom non exclusif.
//
// On POSE UNIQUEMENT ce qui manque (jamais d'écrasement d'une implémentation existante) et on s'appuie
// sur expo-crypto (déjà embarqué → aucun rebuild). Toute défaillance est LOGGÉE fort : un repli
// silencieux en plain (en croyant être en s256) serait le pire scénario.

interface CryptoLike {
  subtle?: { digest?: (algorithm: unknown, data: unknown) => Promise<ArrayBuffer> };
  getRandomValues?: <T>(array: T) => T;
}
export interface PolyfillTarget {
  crypto?: CryptoLike;
  TextEncoder?: unknown;
  btoa?: unknown;
}

// Encodeur UTF-8 minimal (repli seulement si TextEncoder est absent). Le verifier PKCE est de l'ASCII
// (hex), mais on gère l'UTF-8 complet par sûreté.
class MinimalTextEncoder {
  readonly encoding = 'utf-8';
  encode(input = ''): Uint8Array {
    return Uint8Array.from(unescape(encodeURIComponent(input)), (c) => c.charCodeAt(0));
  }
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
function minimalBtoa(input: string): string {
  let out = '';
  for (let i = 0; i < input.length; i += 3) {
    const a = input.charCodeAt(i);
    const b = i + 1 < input.length ? input.charCodeAt(i + 1) : NaN;
    const c = i + 2 < input.length ? input.charCodeAt(i + 2) : NaN;
    out += B64[a >> 2] + B64[((a & 3) << 4) | (isNaN(b) ? 0 : b >> 4)];
    out += isNaN(b) ? '=' : B64[((b & 15) << 2) | (isNaN(c) ? 0 : c >> 6)];
    out += isNaN(c) ? '=' : B64[c & 63];
  }
  return out;
}

async function subtleDigest(algorithm: unknown, data: unknown): Promise<ArrayBuffer> {
  const name = typeof algorithm === 'string' ? algorithm : (algorithm as { name?: string })?.name;
  if (name !== 'SHA-256') throw new Error(`pkcePolyfill: algorithme non supporté (${String(name)})`);
  return ExpoCrypto.digest(ExpoCrypto.CryptoDigestAlgorithm.SHA256, data as BufferSource);
}

export const CODE_INCIDENT_PKCE = 'pkce_s256_indisponible';

// DIFFÉRÉ, et pas par paresse : ce module se charge via services/supabase, AVANT l'appel à
// initSentry() d'App.tsx, et capturerIncident ne dit rien tant que Sentry n'est pas initialisé. Un
// appel immédiat serait donc muet dans le seul build où il doit parler. Le warning console, lui,
// n'est lu par personne en production.
function signalerEchecPkce(): void {
  setTimeout(() => {
    try {
      capturerIncident(CODE_INCIDENT_PKCE, 'oauth');
    } catch (e) {
      console.warn('[pkcePolyfill] remontée de l’échec impossible :', e);
    }
  }, 0);
}

// Pose la brique `label` UNIQUEMENT si absente. Jamais d'écrasement. Retourne false si l'installation
// nécessaire a échoué.
function ensure(present: boolean, label: string, install: () => void): boolean {
  if (present) return true; // déjà fourni (Hermes, autre polyfill) → on ne touche à rien
  try {
    install();
    return true;
  } catch (e) {
    console.warn(`[pkcePolyfill] échec d'installation de ${label} :`, e);
    return false;
  }
}

/**
 * Installe crypto.subtle.digest (+ TextEncoder/btoa si absents) sur `target`, sans écraser l'existant.
 * Retourne true si crypto.subtle.digest est disponible à la fin ; sinon logge un warning EXPLICITE.
 */
export function installPkceCrypto(target: PolyfillTarget): boolean {
  let ok = true;

  ok =
    ensure(typeof target.TextEncoder !== 'undefined', 'TextEncoder', () => {
      target.TextEncoder = MinimalTextEncoder;
    }) && ok;

  ok =
    ensure(typeof target.btoa === 'function', 'btoa', () => {
      target.btoa = minimalBtoa;
    }) && ok;

  ok =
    ensure(typeof target.crypto?.subtle?.digest === 'function', 'crypto.subtle.digest', () => {
      // crypto existe mais sans subtle → on l'augmente sans toucher au reste.
      if (target.crypto && !target.crypto.subtle) {
        try {
          target.crypto.subtle = { digest: subtleDigest };
          return;
        } catch {
          // crypto gelé → on redéfinit crypto complet en préservant getRandomValues existant.
        }
      }
      const existing = target.crypto;
      const getRandomValues =
        existing?.getRandomValues?.bind(existing) ??
        (<T>(array: T): T => ExpoCrypto.getRandomValues(array as never) as T);
      target.crypto = { getRandomValues, subtle: { digest: subtleDigest } };
    }) && ok;

  const active = typeof target.crypto?.subtle?.digest === 'function';
  if (!ok || !active) {
    // DÉFAILLANCE VISIBLE : sans crypto.subtle, auth-js reste en PKCE `plain` (challenge en clair,
    // interceptable). Le durcissement S256 n'est PAS actif.
    console.warn(
      '[pkcePolyfill] crypto.subtle.digest indisponible : le PKCE OAuth reste en `plain` (challenge ' +
        'en clair, interceptable). Le durcissement S256 N’EST PAS actif. Vérifie expo-crypto et l’objet crypto global.',
    );
    signalerEchecPkce();
    return false;
  }
  return true;
}

// Effet de bord au chargement : doit précéder createClient (cf. import dans services/supabase.ts).
installPkceCrypto(globalThis as unknown as PolyfillTarget);
